import React from "react";
import { Link } from "react-router-dom";

/** One step of the trail. The page you are on carries no link. */
export interface Crumb {
  label: string;
  /** Where the step goes. Left off for the current page. */
  to?: string;
}

interface PageHeaderProps {
  /**
   * What the page is called. A node rather than a string only because one
   * screen makes its title editable in place — see `PlanNameField`; every
   * other one passes the text.
   */
  title: React.ReactNode;
  /**
   * Set beside the title, inside the same heading — the plan number after a
   * plan's name. Part of the heading rather than a line under it, because it
   * is part of what the page is called.
   */
  titleSuffix?: React.ReactNode;
  /**
   * The trail back to where this screen was opened from, shown under the
   * title. Only screens that sit under another one pass it — on a section's
   * own page the trail would say no more than the title already does.
   */
  breadcrumbs?: Crumb[];
  /**
   * Buttons set against the right edge, level with the title and the trail
   * under it — an action on the record the whole screen is about rather than on
   * one tab of it.
   *
   * Actions that belong to what a single tab is showing stay in that tab.
   */
  actions?: React.ReactNode;
}

/** Title row shared by every signed-in screen. */
const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  titleSuffix,
  breadcrumbs,
  actions,
}) => (
  // A row only when there are actions to set opposite the title; without them
  // the header is what it always was, so nothing else on the app changes shape.
  <header className="flex-shrink-0 min-w-0 flex flex-wrap items-start justify-between gap-x-4 gap-y-3">
    <div className="min-w-0 flex-1">
      <h1 className="flex min-w-0 items-baseline gap-2 text-[18px] sm:text-[20px] 3xl:text-[24px] font-medium text-ink">
        <span className="truncate">{title}</span>
        {titleSuffix}
      </h1>
      {breadcrumbs && breadcrumbs.length > 0 && (
        <nav aria-label="Breadcrumb" className="mt-1">
          <ol className="flex flex-wrap items-center gap-1 text-[12px] 3xl:text-[13px] text-[#80828D]">
            {breadcrumbs.map((crumb, index) => (
              <li key={crumb.label} className="flex items-center gap-1">
                {crumb.to ? (
                  <Link
                    to={crumb.to}
                    className="hover:text-brand transition-colors"
                  >
                    {crumb.label}
                  </Link>
                ) : (
                  // The last step names the page you are already on, so it is
                  // read out as the current one rather than offered as a way
                  // to somewhere else.
                  <span aria-current="page">{crumb.label}</span>
                )}
                {index < breadcrumbs.length - 1 && (
                  <span aria-hidden="true">&gt;</span>
                )}
              </li>
            ))}
          </ol>
        </nav>
      )}
    </div>

    {actions && (
      <div className="flex flex-shrink-0 flex-wrap items-center gap-2 sm:gap-3">
        {actions}
      </div>
    )}
  </header>
);

export default PageHeader;
